export type QuestionBombModeId =
  | "warmup"
  | "honest"
  | "chaos"
  | "questionOnly"
  | "customOnly";

export type QuestionBombMode = {
  id: QuestionBombModeId;
  shortLabel: string;
  label: string;
  description: string;
  ruleSummary: string;
  answerGuide: string;
  missionEvery: number | null;
  accentColor: "primary" | "secondary" | "success" | "warning";
  settingsOnly?: boolean;
};

export type QuestionBombItem = {
  id: string;
  modeId: QuestionBombModeId;
  category: string;
  text: string;
};

export type QuestionBombCustomItem = QuestionBombItem & {
  createdAt: number;
};

export type QuestionBombMission = {
  id: string;
  modeId: QuestionBombModeId;
  title: string;
  description: string;
  note: string;
};

export type QuestionBombCustomMission = QuestionBombMission & {
  createdAt: number;
};

export const questionBombModes: QuestionBombMode[] = [
  {
    id: "warmup",
    shortLabel: "워밍업",
    label: "워밍업 모드",
    description: "가볍고 부담 없는 질문으로 빠르게 분위기를 풉니다.",
    ruleSummary: "부담 적은 질문 위주, 질문 5개마다 미션 카드가 한 번 끼어듭니다.",
    answerGuide: "짧고 편하게 답해도 충분해요. 다음 사람에게 자연스럽게 넘겨보세요.",
    missionEvery: 5,
    accentColor: "success",
  },
  {
    id: "honest",
    shortLabel: "진심",
    label: "진심 토크 모드",
    description: "조금 더 깊고 솔직한 질문으로 대화를 넓힙니다.",
    ruleSummary: "생각이 필요한 질문 위주, 답변 뒤에 이유 한 문장까지 붙여보세요. 질문 4개마다 미션 카드가 나옵니다.",
    answerGuide: "답을 정한 뒤 이유나 감정을 한 문장 더 붙이면 모드 톤이 더 살아납니다.",
    missionEvery: 4,
    accentColor: "secondary",
  },
  {
    id: "chaos",
    shortLabel: "파티",
    label: "파티 스파크 모드",
    description: "반응이 큰 질문과 장난기 있는 규칙으로 텐션을 올립니다.",
    ruleSummary: "즉답형 질문과 지목형 질문이 섞이고, 질문 4개마다 미션 카드가 등장합니다.",
    answerGuide: "3초 안에 첫 답을 던진다는 느낌으로 가볍고 빠르게 반응해보세요.",
    missionEvery: 4,
    accentColor: "warning",
  },
  {
    id: "questionOnly",
    shortLabel: "질문만",
    label: "질문만 모드",
    description: "미션 없이 질문 카드만 연속으로 뽑는 빠른 플레이용 모드입니다.",
    ruleSummary: "미션 카드가 전혀 나오지 않고 질문만 끝까지 이어집니다.",
    answerGuide: "짧고 빠르게 답하면서 여러 사람에게 연속으로 넘기기 좋아요.",
    missionEvery: null,
    accentColor: "primary",
  },
  {
    id: "customOnly",
    shortLabel: "사용자",
    label: "사용자 설정 모드",
    description: "내가 직접 추가한 질문과 미션만으로 플레이하는 설정 전용 모드입니다.",
    ruleSummary:
      "사용자 질문만 플레이하고, 미션 사용 여부와 등장 주기는 설정에서 직접 바꿀 수 있습니다.",
    answerGuide:
      "우리 모임 취향에 맞게 만든 카드만 이어서 진행하고 싶을 때 쓰기 좋아요.",
    missionEvery: 4,
    accentColor: "secondary",
    settingsOnly: true,
  },
];

export const questionBombItems: QuestionBombItem[] = [
  {
    id: "warmup-01",
    modeId: "warmup",
    category: "아이스브레이킹",
    text: "오늘 여기 오기 전에 은근히 가장 기대했던 순간은 무엇이었나요?",
  },
  {
    id: "warmup-02",
    modeId: "warmup",
    category: "취향",
    text: "요즘 처음 듣는 사람에게도 부담 없이 추천하기 좋은 노래 한 곡은 무엇인가요?",
  },
  {
    id: "warmup-03",
    modeId: "warmup",
    category: "밥 이야기",
    text: "오늘 저녁 메뉴를 마음대로 정할 수 있다면 가장 먼저 떠오르는 음식은 무엇인가요?",
  },
  {
    id: "warmup-04",
    modeId: "warmup",
    category: "여행",
    text: "주말 하루가 통째로 비면 크게 준비하지 않고도 가볍게 다녀오고 싶은 곳은 어디인가요?",
  },
  {
    id: "warmup-05",
    modeId: "warmup",
    category: "TMI",
    text: "오늘 하루 중 예상보다 기분이 좋아졌던 사소한 순간은 무엇이었나요?",
  },
  {
    id: "warmup-06",
    modeId: "warmup",
    category: "취미",
    text: "누가 물어보면 생각보다 신나게 설명하게 되는 내 취미 하나는 무엇인가요?",
  },
  {
    id: "warmup-07",
    modeId: "warmup",
    category: "추억",
    text: "최근 가장 크게 웃었던 일을 장면 하나만 떠올려 이야기해 주세요.",
  },
  {
    id: "warmup-08",
    modeId: "warmup",
    category: "일상",
    text: "아침에 눈뜨자마자 거의 자동으로 하게 되는 첫 루틴은 무엇인가요?",
  },
  {
    id: "warmup-09",
    modeId: "warmup",
    category: "선택",
    text: "평생 한 가지 간식 계열만 고를 수 있다면 달콤한 쪽과 짭짤한 쪽 중 무엇을 고를 건가요?",
  },
  {
    id: "warmup-10",
    modeId: "warmup",
    category: "관계",
    text: "처음 친해질 때 나는 먼저 말을 거는 편인가요, 상대가 다가오길 기다리는 편인가요?",
  },
  {
    id: "warmup-11",
    modeId: "warmup",
    category: "콘텐츠",
    text: "요즘 누군가에게 부담 없이 추천하기 좋은 영상, 예능, 드라마 중 하나가 있나요?",
  },
  {
    id: "warmup-12",
    modeId: "warmup",
    category: "상상",
    text: "딱 하루만 다른 도시의 현지인처럼 살아볼 수 있다면 어디에서 보내고 싶나요?",
  },
  {
    id: "warmup-13",
    modeId: "warmup",
    category: "카페",
    text: "카페에서 자주 고르는 메뉴 하나를 고르고, 그 메뉴가 왜 나다운지 짧게 말해볼 수 있나요?",
  },
  {
    id: "warmup-14",
    modeId: "warmup",
    category: "사진",
    text: "휴대폰 사진첩에서 최근에 저장한 사진 한 장을 떠올리면 어떤 장면이 가장 먼저 생각나나요?",
  },
  {
    id: "warmup-15",
    modeId: "warmup",
    category: "날씨",
    text: "지금 기분을 오늘의 날씨에 비유한다면 맑음, 흐림, 소나기, 선선함 중 무엇에 가깝나요?",
  },
  {
    id: "warmup-16",
    modeId: "warmup",
    category: "휴식",
    text: "예상치 않게 반나절의 자유 시간이 생기면 가장 먼저 하고 싶은 일은 무엇인가요?",
  },
  {
    id: "warmup-17",
    modeId: "warmup",
    category: "작은 재능",
    text: "친구들이 알면 은근히 신기해할 만한 작은 재능이나 생활 스킬이 하나 있나요?",
  },
  {
    id: "warmup-18",
    modeId: "warmup",
    category: "소지품",
    text: "지금 가방이나 주머니에 있는 물건 중 나를 은근히 잘 설명하는 물건 하나를 꼽는다면 무엇인가요?",
  },
  {
    id: "warmup-19",
    modeId: "warmup",
    category: "위로",
    text: "피곤하거나 기운이 없을 때 나를 가장 빨리 편안하게 만드는 간식이나 음료는 무엇인가요?",
  },
  {
    id: "warmup-20",
    modeId: "warmup",
    category: "플레이리스트",
    text: "지금 이 모임 분위기를 플레이리스트 첫 곡으로 표현한다면 어떤 느낌의 노래를 넣고 싶나요?",
  },
  {
    id: "honest-01",
    modeId: "honest",
    category: "진심",
    text: "최근 스스로를 꽤 괜찮은 사람이라고 느꼈던 순간은 언제였나요?",
  },
  {
    id: "honest-02",
    modeId: "honest",
    category: "걱정",
    text: "요즘 머릿속에서 반복해서 맴도는 고민이 있다면 무엇인가요?",
  },
  {
    id: "honest-03",
    modeId: "honest",
    category: "감사",
    text: "최근 고맙다고 꼭 말하고 싶었는데 아직 충분히 표현하지 못한 사람은 누구였나요?",
  },
  {
    id: "honest-04",
    modeId: "honest",
    category: "변화",
    text: "지금의 나에게서 딱 하나만 바꿀 수 있다면 어떤 습관이나 반응 방식을 바꾸고 싶나요?",
  },
  {
    id: "honest-05",
    modeId: "honest",
    category: "회복",
    text: "지쳤을 때 나를 가장 빨리 회복시켜 주는 것은 사람, 공간, 행동 중 무엇에 가까운가요?",
  },
  {
    id: "honest-06",
    modeId: "honest",
    category: "용기",
    text: "최근 나를 조금 더 용감하게 만든 말, 장면, 혹은 누군가의 태도가 있었나요?",
  },
  {
    id: "honest-07",
    modeId: "honest",
    category: "관계",
    text: "가까운 관계일수록 더 지키고 싶은 나만의 기준이나 선이 있다면 무엇인가요?",
  },
  {
    id: "honest-08",
    modeId: "honest",
    category: "미래",
    text: "1년 뒤의 내가 지금의 나에게 가장 고마워할 선택 하나를 꼽는다면 무엇인가요?",
  },
  {
    id: "honest-09",
    modeId: "honest",
    category: "기억",
    text: "아직도 문득 떠오르면 마음이 조금 말랑해지는 기억은 무엇인가요?",
  },
  {
    id: "honest-10",
    modeId: "honest",
    category: "도전",
    text: "사실은 해보고 싶지만 아직 자신 있게 입 밖으로 꺼내지 못한 목표가 있나요?",
  },
  {
    id: "honest-11",
    modeId: "honest",
    category: "위로",
    text: "요즘의 나에게 가장 듣고 싶은 말 한마디가 있다면 무엇인가요?",
  },
  {
    id: "honest-12",
    modeId: "honest",
    category: "후회",
    text: "가볍게라도 다시 선택해보고 싶은 타이밍이나 장면이 있나요?",
  },
  {
    id: "honest-13",
    modeId: "honest",
    category: "오해",
    text: "사람들이 자주 오해하지만 사실은 그렇지 않은 내 모습이 있다면 무엇인가요?",
  },
  {
    id: "honest-14",
    modeId: "honest",
    category: "감정",
    text: "요즘 일부러 모른 척하고 지나가는 감정이 있다면 어떤 감정인가요?",
  },
  {
    id: "honest-15",
    modeId: "honest",
    category: "기준",
    text: "남들이 몰라도 나는 꼭 지키고 싶어 하는 내 기준 하나는 무엇인가요?",
  },
  {
    id: "honest-16",
    modeId: "honest",
    category: "영향",
    text: "지금의 나를 꽤 많이 바꿔놓은 관계나 경험이 있다면 무엇이었나요?",
  },
  {
    id: "honest-17",
    modeId: "honest",
    category: "칭찬",
    text: "누군가에게 들으면 좋지만 막상 들으면 어색해지는 칭찬이 있다면 무엇인가요?",
  },
  {
    id: "honest-18",
    modeId: "honest",
    category: "회복",
    text: "최근 잃어버렸다고 느꼈지만 다시 되찾고 싶은 나만의 감각이나 리듬이 있나요?",
  },
  {
    id: "honest-19",
    modeId: "honest",
    category: "선택",
    text: "올해만큼은 타협하고 싶지 않은 가치나 태도 하나가 있다면 무엇인가요?",
  },
  {
    id: "honest-20",
    modeId: "honest",
    category: "대화",
    text: "사실은 누군가가 먼저 물어봐 주면 솔직히 이야기해보고 싶은 주제가 있나요?",
  },
  {
    id: "chaos-01",
    modeId: "chaos",
    category: "즉답",
    text: "3초 안에 대답하기, 지금 가장 먼저 떠오르는 야식은 무엇인가요?",
  },
  {
    id: "chaos-02",
    modeId: "chaos",
    category: "지목",
    text: "오늘 여기 있는 사람 중 하루를 바꿔 살아보면 가장 재미있는 하루가 될 것 같은 사람은 누구인가요?",
  },
  {
    id: "chaos-03",
    modeId: "chaos",
    category: "흑역사",
    text: "지금 이 자리에서 공개 가능한 가장 순한 흑역사 하나만 말해 주세요.",
  },
  {
    id: "chaos-04",
    modeId: "chaos",
    category: "별명",
    text: "오늘의 내 상태를 기준으로 별명을 붙인다면 어떤 이름이 어울리나요?",
  },
  {
    id: "chaos-05",
    modeId: "chaos",
    category: "상상",
    text: "갑자기 단체 여행을 떠나면 나는 총무, 길찾기 담당, 분위기 담당 중 무엇을 맡게 될까요?",
  },
  {
    id: "chaos-06",
    modeId: "chaos",
    category: "선택",
    text: "평생 야식 하나만 먹어야 한다면 라면과 치킨 중 무엇을 고를 건가요?",
  },
  {
    id: "chaos-07",
    modeId: "chaos",
    category: "개인기",
    text: "완성도와 상관없이 짧게라도 보여줄 수 있는 개인기나 성대모사가 하나 있나요?",
  },
  {
    id: "chaos-08",
    modeId: "chaos",
    category: "밈",
    text: "내 요즘 일상을 가장 잘 설명하는 인터넷 밈이나 반응짤 느낌은 무엇인가요?",
  },
  {
    id: "chaos-09",
    modeId: "chaos",
    category: "추측",
    text: "오늘 이 자리에서 가장 먼저 텐션이 확 올라갈 사람을 한 명 고른다면 누구인가요?",
  },
  {
    id: "chaos-10",
    modeId: "chaos",
    category: "TMI",
    text: "내 휴대폰 검색 기록에서 그나마 안전하게 공개 가능한 키워드 하나는 무엇인가요?",
  },
  {
    id: "chaos-11",
    modeId: "chaos",
    category: "리액션",
    text: "예상치 못한 칭찬을 들었을 때 튀어나오는 내 기본 리액션은 어떤 편인가요?",
  },
  {
    id: "chaos-12",
    modeId: "chaos",
    category: "자신감",
    text: "지금 이 자리에서 내가 가장 자신 있게 추천할 수 있는 것은 무엇인가요?",
  },
  {
    id: "chaos-13",
    modeId: "chaos",
    category: "BGM",
    text: "오늘 내 등장 장면에 배경음악을 붙인다면 어떤 장르나 느낌이 어울리나요?",
  },
  {
    id: "chaos-14",
    modeId: "chaos",
    category: "캐스팅",
    text: "오늘의 나를 영화 캐릭터로 캐스팅한다면 어떤 타입의 인물로 소개할 수 있을까요?",
  },
  {
    id: "chaos-15",
    modeId: "chaos",
    category: "가방 털이",
    text: "지금 내 가방이나 주머니에서 하나 꺼내면 가장 의외일 것 같은 물건은 무엇인가요?",
  },
  {
    id: "chaos-16",
    modeId: "chaos",
    category: "소개팅 예능",
    text: "연애 예능 첫 등장 자막처럼 나를 소개한다면 어떤 문구가 붙을 것 같나요?",
  },
  {
    id: "chaos-17",
    modeId: "chaos",
    category: "팀전",
    text: "방탈출을 간다면 여기 있는 사람 중 누구와 한 팀이 되면 가장 든든할 것 같나요?",
  },
  {
    id: "chaos-18",
    modeId: "chaos",
    category: "배고픔",
    text: "배가 너무 고플 때 나는 귀여워지는 편, 예민해지는 편, 조용해지는 편 중 어디에 가깝나요?",
  },
  {
    id: "chaos-19",
    modeId: "chaos",
    category: "사건 사고",
    text: "이 모임에서 단체로 지각 사태가 난다면 가장 유력한 원인은 무엇일 것 같나요?",
  },
  {
    id: "chaos-20",
    modeId: "chaos",
    category: "생존력",
    text: "갑자기 무인도 예능에 출연하게 되면 내가 맡게 될 역할은 리더, 불평 담당, 분위기 메이커 중 무엇일까요?",
  },
  {
    id: "question-only-01",
    modeId: "questionOnly",
    category: "한 단어",
    text: "요즘의 나를 가장 잘 설명하는 한 단어를 고른다면 무엇인가요?",
  },
  {
    id: "question-only-02",
    modeId: "questionOnly",
    category: "일상",
    text: "오늘 하루 중 다시 한 번 반복하고 싶은 순간이 있다면 언제인가요?",
  },
  {
    id: "question-only-03",
    modeId: "questionOnly",
    category: "취향",
    text: "지금 누군가에게 바로 추천하고 싶은 앱이나 서비스가 하나 있나요?",
  },
  {
    id: "question-only-04",
    modeId: "questionOnly",
    category: "관계",
    text: "요즘 특별한 이유 없이도 가장 자주 연락하게 되는 사람은 누구인가요?",
  },
  {
    id: "question-only-05",
    modeId: "questionOnly",
    category: "음식",
    text: "한 달 동안 무료로 먹을 수 있다면 가장 먼저 고를 음식은 무엇인가요?",
  },
  {
    id: "question-only-06",
    modeId: "questionOnly",
    category: "기분",
    text: "이번 주를 색깔 하나로 표현하면 어떤 색이 가장 먼저 떠오르나요?",
  },
  {
    id: "question-only-07",
    modeId: "questionOnly",
    category: "소비",
    text: "최근 가장 만족스러웠던 작은 소비 하나를 꼽는다면 무엇이었나요?",
  },
  {
    id: "question-only-08",
    modeId: "questionOnly",
    category: "추억",
    text: "문득 떠오르면 아직도 웃음이 나는 장면 하나를 말해 주세요.",
  },
  {
    id: "question-only-09",
    modeId: "questionOnly",
    category: "휴식",
    text: "완전히 쉬는 날이면 꼭 챙기게 되는 나만의 루틴이 있나요?",
  },
  {
    id: "question-only-10",
    modeId: "questionOnly",
    category: "콘텐츠",
    text: "짧은 시간 안에 몰입해서 보기 좋은 콘텐츠 하나를 추천해 주세요.",
  },
  {
    id: "question-only-11",
    modeId: "questionOnly",
    category: "상상",
    text: "내 방이나 책상에 기능 하나를 추가할 수 있다면 무엇을 넣고 싶나요?",
  },
  {
    id: "question-only-12",
    modeId: "questionOnly",
    category: "계획",
    text: "다음 주에 꼭 챙기고 싶은 작고 구체적인 계획 하나가 있나요?",
  },
  {
    id: "question-only-13",
    modeId: "questionOnly",
    category: "소리",
    text: "들으면 이상하게 마음이 차분해지는 소리나 배경음이 있다면 무엇인가요?",
  },
  {
    id: "question-only-14",
    modeId: "questionOnly",
    category: "습관",
    text: "심심할 때 무의식적으로 가장 먼저 열어보는 앱이나 사이트는 무엇인가요?",
  },
  {
    id: "question-only-15",
    modeId: "questionOnly",
    category: "편의성",
    text: "하루 일과 중 한 가지를 자동화할 수 있다면 어떤 일을 가장 먼저 맡기고 싶나요?",
  },
  {
    id: "question-only-16",
    modeId: "questionOnly",
    category: "기억",
    text: "최근 들었던 말 중 생각보다 오래 남아 있는 칭찬이나 한마디가 있나요?",
  },
  {
    id: "question-only-17",
    modeId: "questionOnly",
    category: "작은 성취",
    text: "아무도 몰랐지만 혼자 은근히 뿌듯했던 최근의 작은 성공은 무엇인가요?",
  },
  {
    id: "question-only-18",
    modeId: "questionOnly",
    category: "공간",
    text: "내 방이나 책상에서 지금의 나를 가장 잘 보여주는 한 구석이 있다면 어떤 모습인가요?",
  },
  {
    id: "question-only-19",
    modeId: "questionOnly",
    category: "유지하고 싶은 것",
    text: "요즘의 나에게서 오래 유지하고 싶은 좋은 리듬이나 습관이 있다면 무엇인가요?",
  },
  {
    id: "question-only-20",
    modeId: "questionOnly",
    category: "챕터 제목",
    text: "오늘 하루를 하나의 챕터 제목으로 붙인다면 어떤 제목이 어울릴까요?",
  },
];

export const questionBombMissions: QuestionBombMission[] = [
  {
    id: "warmup-mission-01",
    modeId: "warmup",
    title: "칭찬 릴레이",
    description: "왼쪽 사람의 오늘 분위기나 첫인상에서 좋았던 점을 한 가지씩 짧게 말해보세요.",
    note: "부담 없이 한 문장만 말해도 충분해요.",
  },
  {
    id: "warmup-mission-02",
    modeId: "warmup",
    title: "공통점 3개 찾기",
    description: "가장 가까운 두 사람이 서로의 공통점을 3개 찾아 모두에게 공유해보세요.",
    note: "취향, 습관, 최근 관심사처럼 가벼운 주제가 잘 어울립니다.",
  },
  {
    id: "warmup-mission-03",
    modeId: "warmup",
    title: "한 줄 자기소개 리믹스",
    description: "이름 대신 오늘의 기분을 별명처럼 붙여 한 줄 소개를 해보세요.",
    note: "예: 커피충전 완료한 민지, 주말 기다리는 지훈",
  },
  {
    id: "warmup-mission-04",
    modeId: "warmup",
    title: "추천 속성전",
    description: "각자 지금 바로 추천할 수 있는 장소, 음식, 콘텐츠를 하나씩 빠르게 던져보세요.",
    note: "망설이지 말고 가장 먼저 떠오른 걸 말하면 더 재밌어요.",
  },
  {
    id: "warmup-mission-05",
    modeId: "warmup",
    title: "이모지 기분 체크",
    description: "각자 지금 기분을 이모지 하나로 정하고, 왜 그 이모지를 골랐는지 한 문장만 덧붙여보세요.",
    note: "복잡하게 설명하지 말고 직감대로 고르면 됩니다.",
  },
  {
    id: "warmup-mission-06",
    modeId: "warmup",
    title: "둘은 진실 하나는 장난",
    description: "내 취향이나 습관에 관한 문장 3개를 말하고, 나머지 사람들이 장난 문장 하나를 맞혀보세요.",
    note: "가볍고 귀여운 내용일수록 분위기가 편해져요.",
  },
  {
    id: "warmup-mission-07",
    modeId: "warmup",
    title: "모임 능력치 투표",
    description: "오늘 이 자리에서 길찾기, 계획 세우기, 분위기 띄우기 중 가장 강할 것 같은 사람을 하나씩 지목해보세요.",
    note: "겹쳐도 괜찮고, 이유를 짧게 붙이면 더 재밌어요.",
  },
  {
    id: "warmup-mission-08",
    modeId: "warmup",
    title: "한 단어 이어말하기",
    description: "첫 사람이 아무 단어 하나를 말하면, 다음 사람부터는 그 분위기에 맞는 단어를 하나씩 이어서 말해 작은 장면을 만들어보세요.",
    note: "정답은 없고 연결감만 있으면 충분합니다.",
  },
  {
    id: "honest-mission-01",
    modeId: "honest",
    title: "고마움 한 문장",
    description: "최근 고마웠던 사람을 떠올리고, 직접 보낸다고 생각하며 한 문장만 말해보세요.",
    note: "길게 설명하지 않아도 진심이 전달되면 충분합니다.",
  },
  {
    id: "honest-mission-02",
    modeId: "honest",
    title: "이번 주의 다짐",
    description: "이번 주 안에 나를 위해 꼭 해주고 싶은 작고 현실적인 행동 하나를 선언해보세요.",
    note: "거창하기보다 실제로 가능한 크기가 좋습니다.",
  },
  {
    id: "honest-mission-03",
    modeId: "honest",
    title: "서로의 강점 한 장면",
    description: "지목된 사람의 강점이 잘 드러났던 장면을 하나씩 떠올려 이야기해보세요.",
    note: "구체적인 장면일수록 더 따뜻하게 남습니다.",
  },
  {
    id: "honest-mission-04",
    modeId: "honest",
    title: "위로 카드",
    description: "요즘 지친 사람에게 건네고 싶은 짧은 조언이나 위로를 돌아가며 말해보세요.",
    note: "정답보다 내 경험에서 나온 말이 더 힘이 됩니다.",
  },
  {
    id: "honest-mission-05",
    modeId: "honest",
    title: "미래의 나에게",
    description: "3개월 뒤의 나에게 지금 꼭 전하고 싶은 말 한 문장을 음성메시지처럼 말해보세요.",
    note: "조언이어도 좋고, 응원이거나 부탁이어도 좋아요.",
  },
  {
    id: "honest-mission-06",
    modeId: "honest",
    title: "경계선 선언",
    description: "이번 주만큼은 지키고 싶은 나만의 에너지 경계선 하나를 말해보세요.",
    note: "예: 늦은 밤 연락 줄이기, 쉬는 시간은 죄책감 없이 쉬기",
  },
  {
    id: "honest-mission-07",
    modeId: "honest",
    title: "응원 저장",
    description: "한 사람을 정해서 그 사람에게 실제로 도움이 될 만한 응원이나 격려를 한 문장씩 건네보세요.",
    note: "추상적인 말보다 구체적인 관찰이 더 오래 남아요.",
  },
  {
    id: "honest-mission-08",
    modeId: "honest",
    title: "잘한 일 회수",
    description: "최근 스스로 지나쳐 버린 잘한 일 하나를 말하고, 다른 사람들은 그 장면의 좋은 점을 하나씩 덧붙여보세요.",
    note: "작은 일이어도 괜찮아요. 인정해 주는 시간이 핵심입니다.",
  },
  {
    id: "chaos-mission-01",
    modeId: "chaos",
    title: "10초 광고",
    description: "아무 물건이나 하나 정해서 10초 안에 홈쇼핑 진행자처럼 과장 광고해보세요.",
    note: "과장할수록 재미가 커집니다.",
  },
  {
    id: "chaos-mission-02",
    modeId: "chaos",
    title: "리액션 강제 장착",
    description: "다음 질문을 뽑는 사람은 답하기 전에 효과음이나 감탄사부터 외쳐야 합니다.",
    note: "예: 오호, 잠깐만, 대박 같은 짧은 리액션이면 충분해요.",
  },
  {
    id: "chaos-mission-03",
    modeId: "chaos",
    title: "별명 제조기",
    description: "지금 이 자리에서 가장 먼저 눈에 띄는 특징을 바탕으로 별명 하나를 붙여보세요.",
    note: "놀림보다 귀엽고 가벼운 톤으로 가는 것이 규칙입니다.",
  },
  {
    id: "chaos-mission-04",
    modeId: "chaos",
    title: "즉흥 포즈 챌린지",
    description: "셋을 세면 모두 동시에 같은 감정의 포즈를 취해보고, 가장 몰입한 사람을 뽑아보세요.",
    note: "감정 예시: 당황, 승리, 초집중",
  },
  {
    id: "chaos-mission-05",
    modeId: "chaos",
    title: "금지어 답변",
    description: "다음 질문을 받는 사람은 답하는 동안 '네', '아니요', '음' 중 하나를 말하면 탈락입니다.",
    note: "버벅일수록 웃겨지는 미션이라 부담 없이 즐기면 됩니다.",
  },
  {
    id: "chaos-mission-06",
    modeId: "chaos",
    title: "예능 자막 낭독",
    description: "다음 답변자는 자신의 대답을 예능 프로그램 내레이션처럼 한 톤 과장해서 말해야 합니다.",
    note: "목소리 톤이나 말속도만 바꿔도 충분히 분위기가 살아나요.",
  },
  {
    id: "chaos-mission-07",
    modeId: "chaos",
    title: "동시 지목 배틀",
    description: "셋을 세면 모두 동시에 '오늘 가장 OO할 것 같은 사람'을 지목하고 이유를 한마디씩 말해보세요.",
    note: "OO에는 센터병, 야식왕, 분위기 메이커 같은 단어를 넣으면 좋아요.",
  },
  {
    id: "chaos-mission-08",
    modeId: "chaos",
    title: "팀 구호 만들기",
    description: "지금 이 모임의 텐션을 한 줄 구호로 만들고, 모두 함께 한 번 외쳐본 뒤 다음 질문으로 넘어가세요.",
    note: "유치할수록 기억에 남고, 짧을수록 따라 하기 쉬워요.",
  },
];
